import { createContext, useState, useEffect, useContext } from 'react'
import { supabase } from '../lib/supabaseClient'

const AuthContext = createContext({})

export const useAuth = () => useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isAuthOperationPending, setIsAuthOperationPending] = useState(false)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes (only process if not in the middle of auth operation)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // Skip state updates if we're in the middle of a signup/signin operation
      // This prevents race condition where both the method return AND listener try to read Response body
      if (!isAuthOperationPending) {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [isAuthOperationPending])

  const value = {
    signUp: async (email, password, emailConsent = false) => {
      try {
        console.log('🔵 Starting signup for:', email, 'Consent:', emailConsent)
        setIsAuthOperationPending(true) // Block onAuthStateChange updates during this operation
        
        const result = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              email: email,
              email_consent: emailConsent
            }
          }
        })
        console.log('🟢 Supabase signup complete')
        
        // Manually update state from result (don't wait for onAuthStateChange)
        if (result.data?.session) {
          setSession(result.data.session)
          setUser(result.data.user)
        }
        
        setIsAuthOperationPending(false) // Re-enable onAuthStateChange updates
        
        // If signup successful, trigger verification email and email consent storage
        if (result.data?.user) {
          const backendUrl = process.env.REACT_APP_BACKEND_URL
          const userId = result.data.user.id
          
          // 0. Create user in MongoDB (CRITICAL - must succeed)
          console.log('👤 Creating user in MongoDB...')
          try {
            const syncResponse = await fetch(`${backendUrl}/api/auth/create-user`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                email: email
              })
            })
            const syncData = await syncResponse.json()
            console.log('✅ MongoDB user created:', syncData)
            
            // Check if user got VIP from invite
            if (syncData.user?.is_vip) {
              console.log('👑 User received VIP status from invite!')
            }
          } catch (err) {
            console.error('❌ CRITICAL: Failed to create MongoDB user:', err)
            // Don't block signup, but log the error
          }
          
          // 1. Send beautiful verification email (non-blocking)
          console.log('📧 Triggering verification email...')
          fetch(`${backendUrl}/api/auth/send-verification-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              email: email
            })
          })
            .then(response => response.json())
            .then(data => {
              console.log('✅ Verification email sent:', data)
            })
            .catch(err => {
              console.warn('⚠️ Verification email failed:', err.message)
            })
          
          // 2. Store email consent if user opted in (non-blocking)
          if (emailConsent) {
            console.log('📧 Storing email consent...')
            fetch(`${backendUrl}/api/auth/email-consent`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                email: email,
                consented: true
              })
            })
              .then(response => response.json())
              .then(data => {
                console.log('✅ Email consent stored:', data)
              })
              .catch(err => {
                console.warn('⚠️ Consent storage failed:', err.message)
              })
          }
        }
        
        console.log('🎉 Signup flow complete, returning result')
        return result
      } catch (error) {
        console.error('❌ Signup error:', error)
        setIsAuthOperationPending(false) // Ensure flag is reset on error
        return { data: null, error: { message: error.message || 'Signup failed' } }
      }
    },
    signIn: async (email, password) => {
      try {
        console.log('🔵 Attempting signin for:', email)
        setIsAuthOperationPending(true) // Block onAuthStateChange updates during this operation
        
        const result = await supabase.auth.signInWithPassword({
          email,
          password
        })
        
        // Manually update state from result (don't wait for onAuthStateChange)
        if (result.data?.session) {
          setSession(result.data.session)
          setUser(result.data.user)
        }
        
        setIsAuthOperationPending(false) // Re-enable onAuthStateChange updates
        
        console.log('✅ Signin complete, returning result')
        return result
      } catch (error) {
        console.error('❌ Signin exception:', error)
        setIsAuthOperationPending(false) // Ensure flag is reset on error
        return { data: null, error: { message: error.message || 'Login failed' } }
      }
    },
    signOut: () => supabase.auth.signOut(),
    user,
    session,
    loading,
    supabase  // Expose supabase client for password reset
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
